import { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Button, Table, Modal, Form, Badge, Row, Col, Spinner } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaTasks, FaClock } from "react-icons/fa";
import {
  createWorkPackage,
  updateWorkPackage,
  deleteWorkPackage,
  fetchWorkPackagesByProject,
} from "../../../redux/slices/workPackageSlice";
import { getFullName } from "../../../utils/helpers";
import i18n from "../../../i18n";

const emptyForm = {
  name: "",
  description: "",
  start_date: "",
  end_date: "",
  etis: "",
  deliverables: [],
  member_ids: [],
};

const WorkPackages = ({ project, workPackages = [], isLoading }) => {
  const dispatch = useDispatch();

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deliverableInput, setDeliverableInput] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const projectMembers = useMemo(() => {
    const raw = project?.members || project?.project_members || [];
    return raw.map((projectMember) => {
      const user = projectMember.user || projectMember;
      const id = user.id || projectMember.user_id || projectMember.id;
      const name = getFullName(user) || user.username || projectMember.username || "N/A";
      return { id, name };
    });
  }, [project]);

  const totalEtisUsed = useMemo(
    () => workPackages.reduce((sum, workPackage) => sum + (Number(workPackage.etis) || 0), 0),
    [workPackages]
  );

  const openCreateModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDeliverableInput("");
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (workPackage) => {
    setEditingId(workPackage.id);
    setForm({
      name: workPackage.name,
      description: workPackage.description || "",
      start_date: workPackage.start_date?.slice(0, 10) || "",
      end_date: workPackage.end_date?.slice(0, 10) || "",
      etis: workPackage.etis,
      deliverables: workPackage.deliverables || [],
      member_ids: (workPackage.members || []).map((assignment) => assignment.user_id || assignment.id),
    });
    setDeliverableInput("");
    setError(null);
    setShowModal(true);
  };

  const addDeliverable = () => {
    const value = deliverableInput.trim();
    if (!value) return;
    setForm((prev) => ({ ...prev, deliverables: [...prev.deliverables, value] }));
    setDeliverableInput("");
  };

  const removeDeliverable = (index) => {
    setForm((prev) => ({ ...prev, deliverables: prev.deliverables.filter((_, itemIndex) => itemIndex !== index) }));
  };

  const toggleMember = (userId) => {
    setForm((prev) => ({
      ...prev,
      member_ids: prev.member_ids.includes(userId)
        ? prev.member_ids.filter((id) => id !== userId)
        : [...prev.member_ids, userId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.name || !form.start_date || !form.end_date || !form.etis) {
      setError(i18n.t("components.WorkPackages.errors.fillFields"));
      return;
    }
    if (new Date(form.end_date) < new Date(form.start_date)) {
      setError(i18n.t("components.WorkPackages.errors.dateOrder"));
      return;
    }
    if (Number(form.etis) <= 0) {
      setError(i18n.t("components.WorkPackages.errors.etisPositive"));
      return;
    }

    const payload = {
      name: form.name,
      description: form.description || null,
      start_date: new Date(form.start_date).toISOString(),
      end_date: new Date(form.end_date).toISOString(),
      etis: Number(form.etis),
      deliverables: form.deliverables,
      member_ids: form.member_ids,
    };

    setSaving(true);
    try {
      if (editingId) {
        await dispatch(updateWorkPackage({ workPackageId: editingId, data: payload })).unwrap();
      } else {
        await dispatch(createWorkPackage({ projectId: project.id, data: payload })).unwrap();
      }

      await dispatch(fetchWorkPackagesByProject(project.id));
      setShowModal(false);
    } catch (err) {
      setError(err?.detail || i18n.t("components.WorkPackages.errors.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(i18n.t("components.WorkPackages.errors.confirmDelete"))) return;
    await dispatch(deleteWorkPackage(id));
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
        <div className="d-flex align-items-center gap-2">
          <FaTasks className="text-danger fs-5" />
          <h4 className="mb-0 fw-bold">{i18n.t("components.WorkPackages.title")}</h4>
          {project?.estimated_etis ? (
            <Badge bg="light" text="dark" className="ms-2 border px-2 py-1">
              <FaClock className="me-1 text-muted" />
              {i18n.t("components.WorkPackages.etisAllocated", {
                used: totalEtisUsed.toFixed(2),
                total: project.estimated_etis,
              })}
            </Badge>
          ) : null}
        </div>
        <Button
          size="sm"
          onClick={openCreateModal}
          className="d-flex btn-primary align-items-center gap-1 px-3 py-2 fw-semibold"
        >
          <FaPlus size={12} /> {i18n.t("components.WorkPackages.newButton")}
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="danger" size="sm" />
          <p className="text-muted mt-2 mb-0" style={{ fontSize: "13px" }}>{i18n.t("components.WorkPackages.loading")}</p>
        </div>
      ) : workPackages.length === 0 ? (
        <div className="text-center py-5 text-muted bg-surface rounded-3 border border-dashed">
          <FaTasks size={32} className="text-secondary mb-2 opacity-50" />
          <p className="mb-0">{i18n.t("components.WorkPackages.noData")}</p>
        </div>
      ) : (
        <div className="table-responsive">
          <Table hover className="align-middle mb-0">
            <thead className="table-light text-uppercase fs-7 text-muted">
              <tr>
                <th className="py-3 ps-3">{i18n.t("components.WorkPackages.table.name")}</th>
                <th className="py-3">{i18n.t("components.WorkPackages.table.start")}</th>
                <th className="py-3">{i18n.t("components.WorkPackages.table.end")}</th>
                <th className="py-3">{i18n.t("components.WorkPackages.table.etis")}</th>
                <th className="py-3">{i18n.t("components.WorkPackages.table.members")}</th>
                <th className="py-3 text-end pe-3">{i18n.t("components.WorkPackages.table.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {workPackages.map((workPackage) => (
                <tr key={workPackage.id}>
                  <td className="fw-semibold ps-3 py-3">
                    {workPackage.name}
                    {workPackage.description && (
                      <div className="text-muted fw-normal text-truncate" style={{ maxWidth: "250px", fontSize: "12px" }}>
                        {workPackage.description}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="text-muted" style={{ fontSize: "13px" }}>
                      {workPackage.start_date?.slice(0, 10)}
                    </span>
                  </td>
                  <td>
                    <span className="text-muted" style={{ fontSize: "13px" }}>
                      {workPackage.end_date?.slice(0, 10)}
                    </span>
                  </td>
                  <td>
                    <Badge bg="light" text="dark" className="border px-2 py-1">
                      {workPackage.etis} ETI
                    </Badge>
                  </td>
                  <td>
                    {workPackage.members.length === 0 ? (
                      <span className="txt-main" style={{ fontSize: "12px" }}>{i18n.t("components.WorkPackages.table.none")}</span>
                    ) : (
                      <Badge
                        style={{
                          fontSize: "12px",
                          backgroundColor: "var(--brand-primary)",
                          color: "#000000"
                        }}
                      >
                        {workPackage.members.length} {workPackage.members.length === 1 ? i18n.t("components.WorkPackages.table.member") : i18n.t("components.WorkPackages.table.membersPlural")}
                      </Badge>
                    )}
                  </td>
                  <td className="text-end pe-3">
                    <Button
                      variant="light"
                      size="sm"
                      className="me-2 text-primary border shadow-sm"
                      onClick={() => openEditModal(workPackage)}
                      title={i18n.t("components.WorkPackages.table.edit")}
                    >
                      <FaEdit size={12} />
                    </Button>
                    <Button
                      variant="light"
                      size="sm"
                      className="text-danger border shadow-sm"
                      onClick={() => handleDelete(workPackage.id)}
                      title={i18n.t("components.WorkPackages.table.delete")}
                    >
                      <FaTrash size={12} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="fw-bold fs-5">
            {editingId ? i18n.t("components.WorkPackages.modal.editTitle") : i18n.t("components.WorkPackages.modal.createTitle")}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="pt-3">
            {error && <div className="theme-alert-danger">{error}</div>}

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-secondary" style={{ fontSize: "13px" }}>{i18n.t("components.WorkPackages.modal.nameLabel")}</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={i18n.t("components.WorkPackages.modal.namePlaceholder")}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-secondary" style={{ fontSize: "13px" }}>{i18n.t("components.WorkPackages.modal.descriptionLabel")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={i18n.t("components.WorkPackages.modal.descriptionPlaceholder")}
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold text-secondary" style={{ fontSize: "13px" }}>{i18n.t("components.WorkPackages.modal.startDate")}</Form.Label>
                  <Form.Control
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold text-secondary" style={{ fontSize: "13px" }}>{i18n.t("components.WorkPackages.modal.endDate")}</Form.Label>
                  <Form.Control
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold text-secondary" style={{ fontSize: "13px" }}>{i18n.t("components.WorkPackages.modal.etisLabel")}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={form.etis}
                    onChange={(e) => setForm({ ...form, etis: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-secondary" style={{ fontSize: "13px" }}>{i18n.t("components.WorkPackages.modal.deliverables")}</Form.Label>
              <div className="d-flex gap-2 mb-2">
                <Form.Control
                  value={deliverableInput}
                  onChange={(e) => setDeliverableInput(e.target.value)}
                  placeholder={i18n.t("components.WorkPackages.modal.deliverablePlaceholder")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addDeliverable();
                    }
                  }}
                />
                <Button className="btn btn-primary" type="button" onClick={addDeliverable}>
                  {i18n.t("components.WorkPackages.modal.add")}
                </Button>
              </div>
              <div className="d-flex flex-wrap gap-1">
                {form.deliverables.map((deliverableItem, deliverableIndex) => (
                  <Badge bg="light" text="dark" className="px-2 py-1 border d-flex align-items-center gap-1" key={deliverableIndex}>
                    {deliverableItem}
                    <span style={{ cursor: "pointer", fontSize: "14px" }} className="text-danger ms-1 fw-bold" onClick={() => removeDeliverable(deliverableIndex)}>&times;</span>
                  </Badge>
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="fw-semibold text-secondary" style={{ fontSize: "13px" }}>{i18n.t("components.WorkPackages.modal.assignedMembers")}</Form.Label>
              <div className="border rounded p-3 custom-scrollbar bg-surface bg-opacity-50" style={{ maxHeight: "150px", overflowY: "auto" }}>
                {projectMembers.length === 0 ? (
                  <p className="text-muted mb-0 text-center py-2" style={{ fontSize: "13px" }}>
                    {i18n.t("components.WorkPackages.modal.noProjectMembers")}
                  </p>
                ) : (
                  projectMembers.map((member) => (
                    <Form.Check
                      key={member.id}
                      type="checkbox"
                      id={`work-package-member-${member.id}`}
                      label={member.name}
                      checked={form.member_ids.includes(member.id)}
                      onChange={() => toggleMember(member.id)}
                      className="mb-2"
                    />
                  ))
                )}
              </div>
              <Form.Text className="text-muted" style={{ fontSize: "11px" }}>
                {i18n.t("components.WorkPackages.modal.memberHint")}
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-top-0 pt-0 pb-3">
            <Button variant="light" className="border px-4" onClick={() => setShowModal(false)}>
              {i18n.t("components.WorkPackages.modal.cancel")}
            </Button>
            <Button
              variant="danger"
              type="submit"
              disabled={saving}
              className="px-4 fw-semibold"
              style={{ backgroundColor: "var(--brand-primary)", border: "none" }}
            >
              {saving ? i18n.t("components.WorkPackages.modal.saving") : i18n.t("components.WorkPackages.modal.save")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default WorkPackages;